import React, { useEffect, useState } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { getPedidos, getEnlacePago } from "../../../services/wordpress/micuenta";
import { Pill } from "../Pill";

const PER_PAGE = 10;

const estadoTono = (slug) => {
  if (slug === "completed" || slug === "processing") return "active";
  if (slug === "pending" || slug === "on-hold") return "pending";
  if (slug === "cancelled" || slug === "failed" || slug === "refunded") return "danger";
  return undefined;
};

const formatearFecha = (fecha) => {
  if (!fecha) return "-";
  const d = new Date(fecha);
  return isNaN(d.getTime()) ? fecha : d.toLocaleDateString("es-ES");
};

export const Pedidos = () => {
  const { colors, mode } = useTheme() || { colors: {}, mode: "light" };
  const isDark = mode === "dark";
  const auth = useAuth();

  const [pedidos, setPedidos] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [expandidoId, setExpandidoId] = useState(null);
  const [pagandoId, setPagandoId] = useState(null);
  const [errorPagoId, setErrorPagoId] = useState(null);

  const textPrimary = isDark ? "#f1f5f9" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0";
  const accent = "#ECB236";

  useEffect(() => {
    if (!auth.apiToken || !auth.wpUrl) {
      setCargando(false);
      return;
    }
    let cancelado = false;
    setCargando(true);
    getPedidos({ wpUrl: auth.wpUrl, apiToken: auth.apiToken, page: pagina, perPage: PER_PAGE })
      .then(({ pedidos: data, totalPages }) => {
        if (cancelado) return;
        setPedidos(data);
        setTotalPaginas(totalPages || 1);
      })
      .catch(() => {
        if (!cancelado) setError("No se pudieron cargar los pedidos.");
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.apiToken, auth.wpUrl, pagina]);

  const handlePagar = async (orderId) => {
    setErrorPagoId(null);
    setPagandoId(orderId);
    try {
      const { url } = await getEnlacePago({
        wpUrl: auth.wpUrl,
        apiToken: auth.apiToken,
        orderId,
      });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setErrorPagoId(orderId);
    } finally {
      setPagandoId(null);
    }
  };

  if (cargando && pedidos.length === 0) {
    return <p style={{ fontSize: 13, color: textSecondary, margin: 0 }}>Cargando...</p>;
  }

  if (pedidos.length === 0) {
    return (
      <p style={{ fontSize: 13, color: textSecondary, fontStyle: "italic", margin: 0 }}>
        {error || "No tienes pedidos realizados."}
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {pedidos.map((p) => (
        <div
          key={p.id}
          style={{
            borderRadius: 16,
            border: `1px solid ${borderColor}`,
            padding: "16px 20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: textPrimary }}>Pedido #{p.id}</span>
                <Pill tone={estadoTono(p.estado_slug)}>{p.estado}</Pill>
              </div>
              <span style={{ fontSize: 12, color: textSecondary }}>{formatearFecha(p.fecha)}</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: textPrimary }}>
                {p.total} {p.moneda}
              </span>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setExpandidoId((id) => (id === p.id ? null : p.id))}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: textSecondary,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {expandidoId === p.id ? "Ocultar" : "Ver detalle"}
                </button>
                {p.estado_slug === "pending" && (
                  <button
                    type="button"
                    onClick={() => handlePagar(p.id)}
                    disabled={pagandoId === p.id}
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: accent,
                      background: "none",
                      border: "none",
                      cursor: pagandoId === p.id ? "default" : "pointer",
                      padding: 0,
                    }}
                  >
                    {pagandoId === p.id ? "Generando..." : "Pagar ahora"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {errorPagoId === p.id && (
            <p style={{ margin: "10px 0 0", fontSize: 12, color: "#dc2626" }}>
              No se pudo generar el enlace de pago. Intentá de nuevo.
            </p>
          )}

          {expandidoId === p.id && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${borderColor}` }}>
              {p.items.map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12,
                    color: textSecondary,
                    padding: "4px 0",
                  }}
                >
                  <span>
                    {item.producto} <span style={{ color: textSecondary }}>x{item.cantidad}</span>
                  </span>
                  <span>
                    {item.precio_unidad} {p.moneda} c/u — {item.total_linea} {p.moneda}
                  </span>
                </div>
              ))}

              <div
                style={{
                  borderTop: `1px solid ${borderColor}`,
                  marginTop: 8,
                  paddingTop: 8,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: textSecondary }}>
                  <span>Subtotal</span>
                  <span>{p.subtotal} {p.moneda}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, color: textPrimary }}>
                  <span>Total</span>
                  <span>{p.total} {p.moneda}</span>
                </div>
                {p.metodo_pago && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: textSecondary }}>
                    <span>Método de pago</span>
                    <span>{p.metodo_pago}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {totalPaginas > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, paddingTop: 8 }}>
          <button
            type="button"
            onClick={() => setPagina((p) => Math.max(1, p - 1))}
            disabled={pagina <= 1}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: pagina <= 1 ? textSecondary : accent,
              background: "none",
              border: "none",
              cursor: pagina <= 1 ? "default" : "pointer",
            }}
          >
            ← Anterior
          </button>
          <span style={{ fontSize: 13, color: textSecondary }}>
            Página {pagina} de {totalPaginas}
          </span>
          <button
            type="button"
            onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
            disabled={pagina >= totalPaginas}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: pagina >= totalPaginas ? textSecondary : accent,
              background: "none",
              border: "none",
              cursor: pagina >= totalPaginas ? "default" : "pointer",
            }}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};
